/**
 * Page des paramètres utilisateur.
 * 
 * Contient :
 * - Toggle auto-switch (notifications)
 * - Profil de disponibilité (invitations à jouer)
 * - Section développement (API target, god mode, debug)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { useTabReselect } from '../hooks/useTabReselect';
import { useSystemStatusContext } from '../contexts/SystemStatusContext';
import ProfileSettings from '../components/ProfileSettings';
import BetaSettingsSection from '../components/BetaSettingsSection';
import { isBetaExperienceActive } from '../components/BetaWelcomeModal';
import AccountSecurity from '../components/AccountSecurity';
import MasterDetail from '../components/MasterDetail';
import { PaletteIcon, BellIcon, UserProfileIcon, WrenchIcon, TrainingIcon, LockIcon, ChatIcon } from '../components/Icons';
import type { Session, ThemePreference, HandLayout, TableBase, TableEmblem, DuplicateTableStyle } from '../types/app';
import '../styles/settings.css';
import '../styles/duplicate-table.css';
import { BACKEND_URL } from '../config';

interface DevSettings {
  apiTarget: string;
  apiUrl: string;
  availableTargets: Record<string, string>;
  godMode: boolean;
  formatCoachAdviceMistral?: boolean;
  skipLogicalOverrides?: boolean;
  isDev: boolean;
}

const API_TARGET_LABELS: Record<string, string> = {
  production: 'Production (webtarot.eu)',
  local: 'Local (localhost:3100)',
  docker: 'Docker (host.docker.internal)',
};

type SettingsSection = 'apparence' | 'notifications' | 'profil' | 'compte' | 'entrainement' | 'dev' | 'beta';

const BASE_SECTIONS: { id: SettingsSection; label: string; icon: React.ReactNode }[] = [
  { id: 'apparence', label: 'Apparence', icon: <PaletteIcon size={18} /> },
  { id: 'notifications', label: 'Notifications', icon: <BellIcon size={18} /> },
  { id: 'profil', label: 'Profil', icon: <UserProfileIcon size={18} /> },
  { id: 'compte', label: 'Compte & sécurité', icon: <LockIcon size={18} /> },
  { id: 'entrainement', label: 'Entraînement', icon: <TrainingIcon size={18} /> },
  { id: 'dev', label: 'Développement', icon: <WrenchIcon size={18} /> },
];

interface SettingsProps {
  session: Session;
  onSessionUpdate?: (session: Session) => void;
}

export default function Settings({ session, onSessionUpdate }: SettingsProps) {
  const { preferences, updatePreferences } = useApp();
  const { status } = useSystemStatusContext();
  const sections = useMemo(() => {
    if (!isBetaExperienceActive(status)) return BASE_SECTIONS;
    return [
      ...BASE_SECTIONS.slice(0, 3),
      { id: 'beta' as const, label: 'Bêta-test', icon: <ChatIcon size={18} /> },
      ...BASE_SECTIONS.slice(3),
    ];
  }, [status]);
  const [devSettings, setDevSettings] = useState<DevSettings | null>(null);
  const [devLoading, setDevLoading] = useState(false);
  const [debugEnabled, setDebugEnabled] = useState(() => {
    const val = localStorage.getItem('wt_debug');
    return !!val && val !== '0' && val !== 'false';
  });
  const [activeSection, setActiveSection] = useState<SettingsSection>('apparence');
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  useTabReselect('settings', () => setShowMobileDetail(false));

  const fetchDevSettings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/dev/settings`);
      if (res.ok) setDevSettings(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchDevSettings(); }, [fetchDevSettings]);

  const updateDevSetting = async (body: Record<string, unknown>) => {
    setDevLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dev/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated = await res.json();
        setDevSettings(prev => prev ? { ...prev, ...updated } : prev);
      }
    } catch { /* ignore */ }
    setDevLoading(false);
  };

  const toggleDebug = (enabled: boolean) => {
    setDebugEnabled(enabled);
    if (enabled) {
      localStorage.setItem('wt_debug', '1');
    } else {
      localStorage.removeItem('wt_debug');
    }
  };

  const handleSectionClick = (id: SettingsSection) => {
    setActiveSection(id);
    setShowMobileDetail(true);
  };

  const sectionInfo = sections.find(s => s.id === activeSection);

  const sidebar = (
    <div className="settings-sidebar">
      <div className="settings-sidebar-header">
        <h2>Paramètres</h2>
      </div>
      <div className="settings-sidebar-list">
        {sections.map(section => (
          <div
            key={section.id}
            className={`settings-sidebar-item ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => handleSectionClick(section.id)}
          >
            <span className="settings-sidebar-icon">{section.icon}</span>
            <span className="settings-sidebar-label">{section.label}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'beta':
        return (
          <section className="settings-section">
            <BetaSettingsSection />
          </section>
        );
      case 'apparence':
        return (
          <section className="settings-section">
            <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                <span className="settings-toggle-label">Thème</span>
                <span className="settings-toggle-description">
                  Choisissez l'apparence de l'interface ou laissez votre navigateur décider.
                </span>
              </div>
              <div className="theme-selector">
                {([
                  { value: 'light'  as ThemePreference, icon: '☀️', label: 'Clair' },
                  { value: 'system' as ThemePreference, icon: '💻', label: 'Système' },
                  { value: 'dark'   as ThemePreference, icon: '🌙', label: 'Sombre' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    className={`theme-option${preferences.theme === opt.value ? ' active' : ''}`}
                    onClick={() => updatePreferences({ theme: opt.value })}
                  >
                    <span className="theme-option-icon">{opt.icon}</span>
                    <span className="theme-option-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                <span className="settings-toggle-label">Cartes en main</span>
                <span className="settings-toggle-description">
                  Disposition de vos cartes pendant la partie.
                </span>
              </div>
              <div className="theme-selector">
                {([
                  { value: 'flat' as HandLayout, icon: '━', label: 'Alignées' },
                  { value: 'arc'  as HandLayout, icon: '⌒', label: 'En arc' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    className={`theme-option${preferences.handLayout === opt.value ? ' active' : ''}`}
                    onClick={() => updatePreferences({ handLayout: opt.value })}
                  >
                    <span className="theme-option-icon">{opt.icon}</span>
                    <span className="theme-option-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                <span className="settings-toggle-label">Texture du tapis</span>
                <span className="settings-toggle-description">
                  Choisissez l'apparence du fond de la table de jeu.
                  Les textures à base de cartes s'adaptent automatiquement au thème clair/sombre.
                </span>
              </div>
              <div className="table-bg-selector">
                {([
                  { value: 'neo'             as TableBase, label: 'Neo',            description: 'Gradient vert' },
                  { value: 'felt'            as TableBase, label: 'Feutre',         description: 'Style tapis de jeu' },
                  { value: 'cards-backs'     as TableBase, label: 'Dos de cartes',  description: 'Dos dispersés' },
                  { value: 'cards-all'       as TableBase, label: '78 cartes',      description: 'Jeu complet' },
                  { value: 'cards-trumps'    as TableBase, label: 'Atouts',         description: 'Atouts uniquement' },
                  { value: 'kaleidoscope'    as TableBase, label: 'Kaléidoscope',   description: 'Mandala 12 plis' },
                  { value: 'kaleidoscope-v2' as TableBase, label: 'Kaléidoscope 2', description: 'Mandala 8 plis' },
                  { value: 'legacy-v2'       as TableBase, label: 'WebTarot 2',     description: 'Texture classique' },
                  { value: 'legacy-v1'       as TableBase, label: 'WebTarot 1',     description: 'Bientôt disponible', disabled: true },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    className={`table-bg-option${preferences.tableBase === opt.value ? ' active' : ''}${(opt as { disabled?: boolean }).disabled ? ' disabled' : ''}`}
                    onClick={() => !(opt as { disabled?: boolean }).disabled && updatePreferences({ tableBase: opt.value })}
                    disabled={(opt as { disabled?: boolean }).disabled}
                  >
                    <div className={`table-bg-preview table-bg-preview-${opt.value}`} />
                    <span className="table-bg-option-label">{opt.label}</span>
                    <span className="table-bg-option-desc">{opt.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Plis animés sur le tapis
                </span>
                <span className="settings-toggle-description">
                  Les cartes jouées s'empilent en tas à côté de chaque joueur pendant la partie.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.tableOverlay === 'cards-plis'}
                  onChange={(e) => updatePreferences({ tableOverlay: e.target.checked ? 'cards-plis' : 'none' })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                <span className="settings-toggle-label">Emblème central</span>
                <span className="settings-toggle-description">
                  Logo WT au centre du tapis. Non cumulable avec les kaléidoscopes.
                </span>
              </div>
              <div className="table-bg-selector table-bg-selector-emblem">
                {([
                  { value: 'none'  as TableEmblem, label: 'Aucun',  description: 'Pas d\'emblème' },
                  { value: 'wt-v1' as TableEmblem, label: 'WT',     description: 'Compact' },
                  { value: 'wt-v2' as TableEmblem, label: 'WT v2',  description: 'Large' },
                  { value: 'wt-v3' as TableEmblem, label: 'W/T',    description: 'Empilé' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    className={`table-bg-option${preferences.tableEmblem === opt.value ? ' active' : ''}`}
                    onClick={() => updatePreferences({ tableEmblem: opt.value })}
                  >
                    <div className={`table-bg-preview table-bg-preview-emblem-${opt.value}`} />
                    <span className="table-bg-option-label">{opt.label}</span>
                    <span className="table-bg-option-desc">{opt.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Tapis spécial en duplicate
                </span>
                <span className="settings-toggle-description">
                  En compétition (hors parties libres), remplace votre texture habituelle par un tapis
                  avec marquage des points cardinaux.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.duplicateTableEnabled}
                  onChange={(e) => updatePreferences({ duplicateTableEnabled: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            {preferences.duplicateTableEnabled && (
              <div className="settings-toggle-row settings-toggle-nested" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                  <span className="settings-toggle-label">Style du tapis duplicate</span>
                </div>
                <div className="table-bg-selector table-bg-selector-dup">
                  {([
                    { value: 'dup-felt'   as DuplicateTableStyle, label: 'Feutre',    description: 'Feutre + coins' },
                    { value: 'dup-neo'    as DuplicateTableStyle, label: 'Prestige',  description: 'Or + enseignes' },
                    { value: 'dup-legacy' as DuplicateTableStyle, label: 'Classique', description: 'Blanc + liseret' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      className={`table-bg-option${preferences.duplicateTableStyle === opt.value ? ' active' : ''}`}
                      onClick={() => updatePreferences({ duplicateTableStyle: opt.value })}
                    >
                      <div className={`table-bg-preview table-bg-preview-${opt.value}`} />
                      <span className="table-bg-option-label">{opt.label}</span>
                      <span className="table-bg-option-desc">{opt.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Animation des cartes jouées
                </span>
                <span className="settings-toggle-description">
                  Quand un joueur pose une carte, elle s'envole depuis sa position vers le tapis.
                  Votre carte part de son emplacement exact dans la main ; celles des adversaires glissent depuis le bord de l'écran.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.cardPlayAnimation}
                  onChange={(e) => updatePreferences({ cardPlayAnimation: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Rotation des cartes posées
                </span>
                <span className="settings-toggle-description">
                  Oriente les cartes du pli selon le siège : 90° Est/Ouest, 180° Nord,
                  45° à 5 joueurs. Un léger basculement naturel est toujours appliqué,
                  indépendamment de cette option.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.trickCardRotation}
                  onChange={(e) => updatePreferences({ trickCardRotation: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Icônes vintage (liste des tables)
                </span>
                <span className="settings-toggle-description">
                  Dans la vue « Toutes les tables », affiche des indicateurs géométriques façon WebTarot 1
                  (triangles, carrés, pentagones colorés selon les places occupées).
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.vintageTableIcons}
                  onChange={(e) => updatePreferences({ vintageTableIcons: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>
          </section>
        );

      case 'notifications':
        return (
          <section className="settings-section">
            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Basculer automatiquement vers la partie
                </span>
                <span className="settings-toggle-description">
                  Quand c'est à vous de jouer, l'onglet "Partie en cours" s'active automatiquement.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.autoSwitchOnTurn}
                  onChange={(e) => updatePreferences({ autoSwitchOnTurn: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Messages privés
                </span>
                <span className="settings-toggle-description">
                  Afficher une notification quand vous recevez un message privé et que vous n'êtes pas sur l'onglet Discussions.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.notifyPrivateMessages}
                  onChange={(e) => updatePreferences({ notifyPrivateMessages: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Messages du salon général
                </span>
                <span className="settings-toggle-description">
                  Afficher une notification pour chaque message posté dans le salon général quand vous n'êtes pas sur l'onglet Discussions.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.notifyGlobalChat}
                  onChange={(e) => updatePreferences({ notifyGlobalChat: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Nouvelles tables
                </span>
                <span className="settings-toggle-description">
                  Afficher une notification quand de nouvelles tables de jeu sont créées et que vous n'êtes pas sur l'onglet Tables.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.notifyNewTables}
                  onChange={(e) => updatePreferences({ notifyNewTables: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            <label className="settings-toggle-row">
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Ne pas déranger en partie
                </span>
                <span className="settings-toggle-description">
                  Quand vous êtes en partie, les invitations à jouer sont automatiquement déclinées (une notification vous informe).
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.doNotDisturbInGame}
                  onChange={(e) => updatePreferences({ doNotDisturbInGame: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            {preferences.doNotDisturbInGame && (
              <label className="settings-toggle-row settings-toggle-nested">
                <div className="settings-toggle-info">
                  <span className="settings-toggle-label">
                    Sauf si partie contre robots uniquement
                  </span>
                  <span className="settings-toggle-description">
                    Accepter quand même les invitations si votre partie en cours est uniquement contre des robots.
                  </span>
                </div>
                <div className="settings-toggle-switch-container">
                  <input
                    type="checkbox"
                    className="settings-toggle-input"
                    checked={preferences.allowInviteIfBotsOnly}
                    onChange={(e) => updatePreferences({ allowInviteIfBotsOnly: e.target.checked })}
                  />
                  <span className="settings-toggle-switch" />
                </div>
              </label>
            )}
          </section>
        );

      case 'profil':
        return <ProfileSettings session={session} />;

      case 'compte':
        return (
          <AccountSecurity
            session={session}
            onSessionUpdate={(s) => onSessionUpdate?.(s)}
          />
        );

      case 'entrainement':
        return (
          <section className="settings-section">
            <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="settings-toggle-info" style={{ marginBottom: '12px' }}>
                <span className="settings-toggle-label">Feedback expert (RLHF)</span>
                <span className="settings-toggle-description">
                  En mode entraînement, affichez un panneau pour annoter les suggestions de l'IA
                  et améliorer le modèle. Le feedback passif (comparaison automatique de votre jeu
                  avec celui de l'IA) est collecté en parallèle pour les joueurs de compétition.
                </span>
              </div>
              <div className="theme-selector">
                {([
                  { value: 'none' as const,      icon: '🚫', label: 'Désactivé',     description: 'Pas de feedback' },
                  { value: 'my_turn' as const,    icon: '👤', label: 'Mon tour',       description: 'Feedback quand c\'est à vous' },
                  { value: 'all_turns' as const,  icon: '👥', label: 'Tous les tours', description: 'Feedback à chaque carte jouée' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    className={`theme-option${preferences.feedbackLevel === opt.value ? ' active' : ''}`}
                    onClick={() => updatePreferences({ feedbackLevel: opt.value })}
                  >
                    <span className="theme-option-icon">{opt.icon}</span>
                    <span className="theme-option-label">{opt.label}</span>
                    <span className="theme-option-desc" style={{ fontSize: '11px', opacity: 0.7 }}>{opt.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-toggle-row" style={{ marginTop: '20px' }}>
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">Modèle beta (expérimental)</span>
                <span className="settings-toggle-description">
                  Utiliser le modèle IA beta au lieu du modèle de production.
                  Ce modèle peut être instable — utile pour tester de nouvelles versions.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={preferences.useBetaModel}
                  onChange={(e) => updatePreferences({ useBetaModel: e.target.checked })}
                />
                <span className="settings-toggle-switch" />
              </div>
            </div>
          </section>
        );

      case 'dev':
        return (
          <section className="settings-section">
            {devSettings && (
              <div className="settings-dev-group">
                <div className="settings-toggle-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="settings-toggle-info" style={{ marginBottom: '10px' }}>
                    <span className="settings-toggle-label">
                      Source de l'API WebtarotAPI
                    </span>
                    <span className="settings-toggle-description">
                      Choisissez entre l'API de production et une instance locale pour le développement.
                    </span>
                  </div>
                  <div className="settings-api-targets">
                    {Object.entries(devSettings.availableTargets).map(([key, url]) => (
                      <button
                        key={key}
                        className={`settings-api-target-btn ${devSettings.apiTarget === key ? 'active' : ''}`}
                        disabled={devLoading}
                        onClick={() => updateDevSetting({ apiTarget: key })}
                      >
                        <span className="settings-api-target-name">
                          {API_TARGET_LABELS[key] || key}
                        </span>
                        <span className="settings-api-target-url">{url}</span>
                      </button>
                    ))}
                  </div>
                  {devSettings.apiTarget === 'custom' && (
                    <div className="settings-api-custom-url">
                      URL personnalisée : {devSettings.apiUrl}
                    </div>
                  )}
                </div>
              </div>
            )}

            {devSettings && (
              <label className="settings-toggle-row" style={{ marginTop: '8px' }}>
                <div className="settings-toggle-info">
                  <span className="settings-toggle-label">
                    Mode Dieu
                  </span>
                  <span className="settings-toggle-description">
                    Accepte n'importe quel login sans vérification du mot de passe.
                  </span>
                </div>
                <div className="settings-toggle-switch-container">
                  <input
                    type="checkbox"
                    className="settings-toggle-input"
                    checked={devSettings.godMode}
                    disabled={devLoading}
                    onChange={(e) => updateDevSetting({ godMode: e.target.checked })}
                  />
                  <span className="settings-toggle-switch" />
                </div>
              </label>
            )}

            {devSettings && (
              <label className="settings-toggle-row" style={{ marginTop: '8px' }}>
                <div className="settings-toggle-info">
                  <span className="settings-toggle-label">
                    Formater le conseil du professeur (Mistral)
                  </span>
                  <span className="settings-toggle-description">
                    Utilise Mistral pour reformuler le conseil de carte de manière pédagogique. Désactiver en développement pour économiser des tokens.
                  </span>
                </div>
                <div className="settings-toggle-switch-container">
                  <input
                    type="checkbox"
                    className="settings-toggle-input"
                    checked={devSettings.formatCoachAdviceMistral !== false}
                    disabled={devLoading}
                    onChange={(e) => updateDevSetting({ formatCoachAdviceMistral: e.target.checked })}
                  />
                  <span className="settings-toggle-switch" />
                </div>
              </label>
            )}

            {devSettings && (
              <label className="settings-toggle-row" style={{ marginTop: '8px' }}>
                <div className="settings-toggle-info">
                  <span className="settings-toggle-label">
                    Désactiver les contraintes logiques (déductions ML)
                  </span>
                  <span className="settings-toggle-description">
                    Affiche les prédictions brutes du modèle ML sans corrections post-traitement. Utile pour évaluer la qualité intrinsèque du modèle.
                  </span>
                </div>
                <div className="settings-toggle-switch-container">
                  <input
                    type="checkbox"
                    className="settings-toggle-input"
                    checked={devSettings.skipLogicalOverrides === true}
                    disabled={devLoading}
                    onChange={(e) => updateDevSetting({ skipLogicalOverrides: e.target.checked })}
                  />
                  <span className="settings-toggle-switch" />
                </div>
              </label>
            )}

            <label className="settings-toggle-row" style={{ marginTop: '8px' }}>
              <div className="settings-toggle-info">
                <span className="settings-toggle-label">
                  Logs de debug
                </span>
                <span className="settings-toggle-description">
                  Active les logs détaillés dans la console du navigateur.
                </span>
              </div>
              <div className="settings-toggle-switch-container">
                <input
                  type="checkbox"
                  className="settings-toggle-input"
                  checked={debugEnabled}
                  onChange={(e) => toggleDebug(e.target.checked)}
                />
                <span className="settings-toggle-switch" />
              </div>
            </label>

            {devSettings && (
              <div className="settings-dev-info">
                <span>Neo backend : {BACKEND_URL}</span>
                <span>WebtarotAPI : {devSettings.apiUrl}</span>
              </div>
            )}
          </section>
        );
    }
  };

  const detail = (
    <div className="settings-detail">
      <div className="settings-detail-header">
        <h2>{sectionInfo?.label}</h2>
      </div>
      <div className="settings-detail-content">
        {renderSectionContent()}
      </div>
    </div>
  );

  return (
    <div className="settings-container">
      <MasterDetail
        master={sidebar}
        detail={detail}
        detailTitle={sectionInfo?.label}
        hasSelection={showMobileDetail}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}
